<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import { computed, reactive, ref } from "vue";

import {
  listWorkers,
  updateTaskBrief,
  type ProjectDetail,
  type ProjectHazard,
  type WorkPackRow,
  type WorkPackTaskBrief,
  type WorkPackTemplate,
} from "@/api/client";
import SiteHazardsNotice from "@/components/SiteHazardsNotice.vue";
import { hasTaskBrief } from "@/lib/workPackSections";

const props = defineProps<{
  pack: WorkPackRow;
  templates?: WorkPackTemplate[];
  project?: ProjectDetail;
}>();
const emit = defineEmits<{ saved: [] }>();

// The starting brief for this pack's task type, if we have a template for it.
const templateBrief = computed(() => {
  if (!props.pack.task_type || !props.templates) return null;
  return (
    props.templates.find((t) => t.task_type === props.pack.task_type)?.brief ??
    null
  );
});

// The supervisor is usually one of the app's people — offer a type-ahead of
// worker names while keeping the field free-text (and blank by default), so a
// name that isn't yet a worker record can still be entered.
const { data: workers } = useQuery({
  queryKey: ["workers", "active"],
  queryFn: () => listWorkers(true),
  staleTime: 5 * 60 * 1000,
});
const supervisorNames = computed(() =>
  [...new Set((workers.value ?? []).map((w) => w.name).filter(Boolean))].sort(
    (a, b) => a.localeCompare(b),
  ),
);

// Merge the task-type defaults into the form without clobbering existing
// entries: union PPE, append hazards/permits not already present.
function prefillFromTaskType(): void {
  const d = templateBrief.value;
  if (!d) return;
  for (const item of d.ppe ?? []) {
    if (!form.ppe.includes(item)) form.ppe.push(item);
  }
  const haveHazards = new Set(
    form.hazards.map((h) => h.hazard.trim().toLowerCase()),
  );
  for (const h of d.hazards ?? []) {
    if (!haveHazards.has(h.hazard.trim().toLowerCase())) {
      form.hazards.push({ ...h });
    }
  }
  const havePermits = new Set(
    form.permits.map((p) => p.type.trim().toLowerCase()),
  );
  for (const p of d.permits ?? []) {
    if (p.type && !havePermits.has(p.type.trim().toLowerCase())) {
      form.permits.push({ ...p });
    }
  }
}

// Site hazards from the project register — pre-ticked onto new briefs and
// offered as one-click adds (RAMS "typed once, used everywhere").
const activeHazards = computed<ProjectHazard[]>(() =>
  (props.project?.hazards ?? []).filter((h) => !h.archived_at),
);
function hazardText(h: ProjectHazard): string {
  const head = h.location ? `${h.category} - ${h.location}` : h.category;
  return `${head}: ${h.description}`;
}
function addSiteHazard(h: ProjectHazard): void {
  const text = hazardText(h);
  if (!form.hazards.some((r) => r.hazard.trim() === text)) {
    form.hazards.push({ hazard: text, control: "" });
  }
}
function addAllSiteHazards(): void {
  for (const h of activeHazards.value) addSiteHazard(h);
}

// The standard rail PPE set offered as toggles; supervisors add anything else.
const STANDARD_PPE = [
  "Hard hat",
  "Hi-vis",
  "Safety boots",
  "Gloves",
  "Eye protection",
  "Hearing protection",
  "Respirator (RPE)",
  "Harness / lanyard",
];

const readOnly = computed(() => props.pack.status === "issued");
const present = computed(() => hasTaskBrief(props.pack));

const editing = ref(false);
const busy = ref(false);
const errorMsg = ref<string | null>(null);
const savedMsg = ref(false);
const customPpe = ref("");

// The editable copy — a deep-ish clone so cancel discards cleanly.
const form = reactive<Required<WorkPackTaskBrief>>({
  description: "",
  location: "",
  supervisor: "",
  hazards: [],
  ppe: [],
  plant: [],
  permits: [],
  notes: "",
});

function startEdit(): void {
  const b = props.pack.task_brief ?? {};
  form.description = b.description ?? "";
  // Pre-fill the exact location from the project's site when unset, as a
  // starting point the briefer can refine (e.g. to "Loc 47, Up Main").
  form.location = b.location ?? props.project?.location_name ?? "";
  form.supervisor = b.supervisor ?? "";
  form.hazards = (b.hazards ?? []).map((h) => ({ ...h }));
  form.ppe = [...(b.ppe ?? [])];
  form.plant = (b.plant ?? []).map((p) => ({ ...p }));
  form.permits = (b.permits ?? []).map((p) => ({ ...p }));
  form.notes = b.notes ?? "";
  errorMsg.value = null;
  savedMsg.value = false;
  customPpe.value = "";
  editSnapshot.value = formJson();
  // Pre-tick the project's site hazards onto a fresh brief (leaves it "unsaved"
  // so the briefer reviews + saves). Existing briefs are left untouched.
  if (!form.hazards.length) addAllSiteHazards();
  editing.value = true;
}

// A stable JSON view of the form, for unsaved-changes detection.
function formJson(): string {
  return JSON.stringify({
    description: form.description.trim(),
    location: form.location.trim(),
    supervisor: form.supervisor.trim(),
    hazards: form.hazards,
    ppe: form.ppe,
    plant: form.plant,
    permits: form.permits,
    notes: form.notes.trim(),
  });
}
const editSnapshot = ref("");
const dirty = computed(
  () => editing.value && formJson() !== editSnapshot.value,
);

function togglePpe(item: string): void {
  const i = form.ppe.indexOf(item);
  if (i === -1) form.ppe.push(item);
  else form.ppe.splice(i, 1);
}
function addCustomPpe(): void {
  const v = customPpe.value.trim();
  if (v && !form.ppe.includes(v)) form.ppe.push(v);
  customPpe.value = "";
}

async function save(): Promise<void> {
  busy.value = true;
  errorMsg.value = null;
  try {
    // Drop fully-empty rows so we don't persist blanks.
    const brief: WorkPackTaskBrief = {
      description: form.description.trim() || undefined,
      location: form.location.trim() || undefined,
      supervisor: form.supervisor.trim() || undefined,
      hazards: form.hazards.filter((h) => h.hazard.trim() || h.control.trim()),
      ppe: form.ppe,
      plant: form.plant.filter((p) => p.item.trim() || p.tag.trim()),
      permits: form.permits.filter((p) => p.type.trim() || p.reference.trim()),
      notes: form.notes.trim() || undefined,
    };
    await updateTaskBrief(props.pack.id, brief);
    editing.value = false;
    savedMsg.value = true;
    emit("saved");
  } catch (e) {
    errorMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}

const brief = computed(() => props.pack.task_brief);
</script>

<template>
  <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-5">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 class="flex items-center gap-2 font-semibold">
          Task Brief
          <span
            class="rounded px-1.5 py-0.5 text-[10px] font-bold"
            :class="present
              ? 'bg-semantic-success/10 text-semantic-success'
              : 'bg-semantic-warning/10 text-semantic-warning'"
          >{{ present ? "✓" : "To do" }}</span>
        </h2>
        <p class="text-sm text-neutral-400">
          The pre-shift briefing — hazards &amp; controls, PPE, plant and permits.
        </p>
      </div>
      <button
        v-if="!readOnly && !editing"
        type="button"
        class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
        @click="startEdit"
      >
        {{ present ? "Edit" : "+ Build brief" }}
      </button>
    </div>

    <!-- ===== Read view ===== -->
    <div
      v-if="!editing"
      class="mt-3"
    >
      <p
        v-if="savedMsg"
        class="mb-2 text-sm font-semibold text-semantic-success"
      >
        ✓ Task brief saved.
      </p>
      <p
        v-if="!present"
        class="text-sm text-neutral-400"
      >
        No task brief yet.
      </p>
      <div
        v-else
        class="grid gap-4 text-sm"
      >
        <div v-if="brief?.description || brief?.location || brief?.supervisor">
          <p
            v-if="brief?.description"
            class="text-neutral-900"
          >
            {{ brief.description }}
          </p>
          <p class="mt-1 text-neutral-500">
            <span v-if="brief?.location">📍 {{ brief.location }}</span>
            <span v-if="brief?.location && brief?.supervisor"> · </span>
            <span v-if="brief?.supervisor">Supervisor: {{ brief.supervisor }}</span>
          </p>
        </div>

        <div v-if="(brief?.hazards ?? []).length">
          <h3 class="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Hazards &amp; controls
          </h3>
          <ul class="grid gap-1">
            <li
              v-for="(h, i) in brief?.hazards"
              :key="'h' + i"
              class="flex gap-2"
            >
              <span class="font-medium text-neutral-900">{{ h.hazard }}</span>
              <span
                v-if="h.control"
                class="text-neutral-500"
              >→ {{ h.control }}</span>
            </li>
          </ul>
        </div>

        <div v-if="(brief?.ppe ?? []).length">
          <h3 class="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Required PPE
          </h3>
          <div class="flex flex-wrap gap-1.5">
            <span
              v-for="item in brief?.ppe"
              :key="item"
              class="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs text-neutral-700"
            >{{ item }}</span>
          </div>
        </div>

        <div v-if="(brief?.plant ?? []).length">
          <h3 class="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Plant &amp; equipment
          </h3>
          <ul class="grid gap-1">
            <li
              v-for="(p, i) in brief?.plant"
              :key="'p' + i"
              class="flex gap-2"
            >
              <span class="font-medium text-neutral-900">{{ p.item }}</span>
              <span
                v-if="p.tag"
                class="text-neutral-500"
              >· {{ p.tag }}</span>
            </li>
          </ul>
        </div>

        <div v-if="(brief?.permits ?? []).length">
          <h3 class="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Permits
          </h3>
          <ul class="grid gap-1">
            <li
              v-for="(p, i) in brief?.permits"
              :key="'pm' + i"
              class="flex gap-2"
            >
              <span class="font-medium text-neutral-900">{{ p.type }}</span>
              <span
                v-if="p.reference"
                class="text-neutral-500"
              >· {{ p.reference }}</span>
            </li>
          </ul>
        </div>

        <p
          v-if="brief?.notes"
          class="whitespace-pre-line text-neutral-600"
        >
          {{ brief.notes }}
        </p>
      </div>
    </div>

    <!-- ===== Edit view ===== -->
    <div
      v-else
      class="mt-4 grid gap-5"
    >
      <!-- Pre-fill from the task type's template -->
      <div
        v-if="templateBrief"
        class="flex items-center justify-between gap-3 rounded-lg bg-brand-primary/5 px-4 py-2.5 text-sm"
      >
        <span class="text-neutral-600">
          Start from the standard <strong>{{ pack.task_type }}</strong> brief
          (PPE, hazards, permits) and edit.
        </span>
        <button
          type="button"
          class="shrink-0 rounded-md border border-brand-primary/40 px-3 py-1.5 font-medium text-brand-primary hover:bg-brand-primary/10"
          @click="prefillFromTaskType"
        >
          Pre-fill
        </button>
      </div>

      <!-- Task information -->
      <div class="grid gap-3">
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Work to be carried out</span>
          <textarea
            v-model="form.description"
            rows="2"
            placeholder="Describe the task for the shift…"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          />
        </label>
        <div class="grid gap-3 sm:grid-cols-2">
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Exact location</span>
            <input
              v-model="form.location"
              type="text"
              placeholder="Loc 47, Up Main"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Supervisor</span>
            <input
              v-model="form.supervisor"
              type="text"
              list="wp-supervisor-names"
              autocomplete="off"
              placeholder="Start typing a name…"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
            <datalist id="wp-supervisor-names">
              <option
                v-for="name in supervisorNames"
                :key="name"
                :value="name"
              />
            </datalist>
          </label>
        </div>
      </div>

      <!-- Hazards & controls -->
      <div>
        <SiteHazardsNotice
          v-if="activeHazards.length"
          class="mb-3"
          :hazards="activeHazards"
          @add="addSiteHazard"
          @add-all="addAllSiteHazards"
        />
        <div class="mb-2 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-neutral-700">
            Hazards &amp; controls
          </h3>
          <button
            type="button"
            class="text-xs text-brand-primary hover:underline"
            @click="form.hazards.push({ hazard: '', control: '' })"
          >
            + Add hazard
          </button>
        </div>
        <p
          v-if="!form.hazards.length"
          class="text-sm text-neutral-400"
        >
          No hazards added.
        </p>
        <div
          v-for="(h, i) in form.hazards"
          :key="'eh' + i"
          class="mb-2 flex gap-2"
        >
          <input
            v-model="h.hazard"
            type="text"
            placeholder="Hazard"
            class="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <input
            v-model="h.control"
            type="text"
            placeholder="Control measure / WMS"
            class="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <button
            type="button"
            class="text-neutral-300 hover:text-semantic-danger"
            title="Remove"
            @click="form.hazards.splice(i, 1)"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- PPE -->
      <div>
        <h3 class="mb-2 text-sm font-semibold text-neutral-700">
          Required PPE
        </h3>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="item in STANDARD_PPE"
            :key="item"
            type="button"
            class="rounded-full px-3 py-1 text-xs font-medium transition"
            :class="form.ppe.includes(item)
              ? 'bg-brand-primary text-brand-primary-on'
              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'"
            @click="togglePpe(item)"
          >
            {{ item }}
          </button>
          <!-- custom PPE already chosen but not in the standard set -->
          <button
            v-for="item in form.ppe.filter((p) => !STANDARD_PPE.includes(p))"
            :key="'c' + item"
            type="button"
            class="rounded-full bg-brand-primary px-3 py-1 text-xs font-medium text-brand-primary-on"
            @click="togglePpe(item)"
          >
            {{ item }} ✕
          </button>
        </div>
        <div class="mt-2 flex gap-2">
          <input
            v-model="customPpe"
            type="text"
            placeholder="Add other PPE…"
            class="w-48 rounded-md border border-neutral-300 px-3 py-1.5 text-sm"
            @keyup.enter="addCustomPpe"
          >
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50"
            @click="addCustomPpe"
          >
            Add
          </button>
        </div>
      </div>

      <!-- Plant & equipment -->
      <div>
        <div class="mb-2 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-neutral-700">
            Plant &amp; equipment
          </h3>
          <button
            type="button"
            class="text-xs text-brand-primary hover:underline"
            @click="form.plant.push({ item: '', tag: '' })"
          >
            + Add item
          </button>
        </div>
        <p
          v-if="!form.plant.length"
          class="text-sm text-neutral-400"
        >
          No plant added.
        </p>
        <div
          v-for="(p, i) in form.plant"
          :key="'ep' + i"
          class="mb-2 flex gap-2"
        >
          <input
            v-model="p.item"
            type="text"
            placeholder="Plant / equipment"
            class="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <input
            v-model="p.tag"
            type="text"
            placeholder="Inspection / tag status"
            class="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <button
            type="button"
            class="text-neutral-300 hover:text-semantic-danger"
            title="Remove"
            @click="form.plant.splice(i, 1)"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- Permits -->
      <div>
        <div class="mb-2 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-neutral-700">
            Permits &amp; sign-offs
          </h3>
          <button
            type="button"
            class="text-xs text-brand-primary hover:underline"
            @click="form.permits.push({ type: '', reference: '' })"
          >
            + Add permit
          </button>
        </div>
        <p
          v-if="activeHazards.length"
          class="mb-2 text-xs text-semantic-warning"
        >
          ⚠ {{ activeHazards.length }} site hazard(s) recorded — ensure the
          permits cover them.
        </p>
        <p
          v-if="!form.permits.length"
          class="text-sm text-neutral-400"
        >
          No permits added.
        </p>
        <div
          v-for="(p, i) in form.permits"
          :key="'epm' + i"
          class="mb-2 flex gap-2"
        >
          <input
            v-model="p.type"
            type="text"
            placeholder="Permit type (hot work, confined space…)"
            class="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <input
            v-model="p.reference"
            type="text"
            placeholder="Reference / number"
            class="w-40 rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <button
            type="button"
            class="text-neutral-300 hover:text-semantic-danger"
            title="Remove"
            @click="form.permits.splice(i, 1)"
          >
            ✕
          </button>
        </div>
      </div>

      <label class="grid gap-1 text-sm">
        <span class="text-neutral-500">Notes</span>
        <textarea
          v-model="form.notes"
          rows="2"
          placeholder="Anything else to brief…"
          class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
        />
      </label>

      <div class="flex items-center gap-3">
        <button
          type="button"
          class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
          :disabled="busy || !dirty"
          @click="save"
        >
          {{ busy ? "Saving…" : dirty ? "Save brief" : "Saved" }}
        </button>
        <button
          type="button"
          class="text-sm text-neutral-500 hover:text-neutral-900"
          @click="editing = false"
        >
          Cancel
        </button>
        <span
          v-if="dirty"
          class="text-xs font-semibold text-semantic-warning"
        >● Unsaved changes</span>
        <span
          v-if="errorMsg"
          class="text-sm text-semantic-danger"
        >{{ errorMsg }}</span>
      </div>
    </div>
  </section>
</template>
